/**
 * PhoneFrame — device shell for presenting v2 screens. Ported from the OnFlow
 * Design System project (v2/components/track/PhoneFrame.jsx).
 *
 * 390×844 (iPhone 14) content area, status bar, home indicator. Screens
 * position their sheets against this.
 */
import type { CSSProperties, ReactNode } from 'react';

interface PhoneFrameProps {
  label?: string;
  time?: string;
  children?: ReactNode;
  style?: CSSProperties;
}

export function PhoneFrame({ label, time = '9:41', children, style }: PhoneFrameProps) {
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', ...style }}
    >
      <div
        style={{
          width: 390 + 20,
          height: 844 + 20,
          borderRadius: 56,
          padding: 10,
          background: 'linear-gradient(160deg, #3a3530 0%, #17150f 40%, #2b2620 100%)',
          boxShadow: '0 40px 80px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.14)',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: 390,
            height: 844,
            borderRadius: 46,
            overflow: 'hidden',
            background: 'var(--paper-0)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              height: 50,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 28px',
              position: 'relative',
              zIndex: 20,
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-ui)',
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--ink-1)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {time}
            </span>
            <span
              aria-hidden
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                top: 8,
                width: 92,
                height: 28,
                borderRadius: 999,
                background: '#000',
              }}
            />
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="17" height="11" viewBox="0 0 17 11" aria-hidden>
                <g fill="var(--ink-1)">
                  {[0, 1, 2, 3].map((i) => (
                    <rect
                      key={i}
                      x={i * 4.4}
                      y={8 - i * 2.4}
                      width="3"
                      height={3 + i * 2.4}
                      rx="1"
                    />
                  ))}
                </g>
              </svg>
              <svg width="24" height="12" viewBox="0 0 24 12" aria-hidden>
                <rect
                  x="0.5"
                  y="0.5"
                  width="20"
                  height="11"
                  rx="3"
                  fill="none"
                  stroke="var(--ink-1)"
                  opacity=".5"
                />
                <rect x="2" y="2" width="15" height="8" rx="2" fill="var(--ink-1)" />
                <rect x="22" y="4" width="2" height="4" rx="1" fill="var(--ink-1)" opacity=".5" />
              </svg>
            </span>
          </div>
          {children}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              bottom: 7,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 132,
              height: 5,
              borderRadius: 999,
              background: 'var(--ink-1)',
              opacity: 0.28,
              zIndex: 30,
            }}
          />
        </div>
      </div>
      {label && (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--ink-3)',
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}
